import React, { Suspense, useState, useEffect, useRef, useMemo } from "react";
import { Await, useLoaderData, useRevalidator, useRouteLoaderData } from "react-router-dom";
import {
    Button,
    Col,
    Table,
    Container,
    Form,
    Row,
    Collapse,
    ButtonGroup,
    Stack,
    InputGroup,
    ToggleButton,
    ToggleButtonGroup,
    Card,
    OverlayTrigger,
    Tooltip,
} from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { axiosInstance } from "../../auth/Auth";
import LoadingIndicator from "../../components/LoadingIndicator";
import { CaretDownFill, CaretUpFill, PencilFill, TrashFill, XLg, CheckLg, QuestionCircleFill, Grid3x3GapFill, Search, List } from "react-bootstrap-icons";
import "./styles.css";
import ConfirmPrompt from "../../components/ConfirmPrompt";
import useSortableData from "../../components/useSortableData";
import { findRadioDayparts, formatDaypartText } from "../../components/HelperFunctions";
import ErrorResponse from "../../components/ErrorResponse";
import FrequencyPicker from "../../components/FrequencyPicker";
import WeeksPicker from "../../components/WeeksPicker";
import MinMaxSpinsPicker from "../../components/MinMaxSpinsPicker";
import EmailAddressPicker from "../../components/EmailAdressPicker";
import { toast } from "react-toastify";

const MusicTestingReports = () => {
    const [errorCode, setErrorCode] = useState(null);
    const { radios, competitorRadios, userInfo, dayparts, demographics } = useRouteLoaderData("root");
    const { reports } = useLoaderData();
    const [reportsData, setReportsData] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [deleteReportId, setDeleteReportId] = useState(null);
    const [allRadios, setAllRadios] = useState([]);
    const emailInputRef = useRef(null);
    const emailInputChangeRef = useRef(null);
    const revalidator = useRevalidator();
    const moduleName = "musicTestingReports";

    const emptyFormData = {
        station: [],
        email_address: [],
        run_frequency: 2,
        number_weeks: 2,
        daypartId: 1,
        minSpins: 0,
        maxSpins: 0,
        demo_id: 1,
        daypart_id: "placeholder",
    };

    const [formData, setFormData] = useState(emptyFormData);

    useEffect(() => {
        async function setData() {
            const resolvedReports = (await reports).data;
            setReportsData(resolvedReports);
        }
        setData();
    }, [reports]);

    useEffect(() => {
        async function setUserEmail() {
            const userData = (await userInfo).data;
            if (userData.email) {
                setFormData(current => ({
                    ...current,
                    email_address: [{ email: userData.email }],
                }));
            }
        }
        setUserEmail();
    }, [userInfo]);

    async function handleFormSubmit(event) {
        event.preventDefault();
        const resolvedUser = (await userInfo).data;
        const input = {
            user_id: resolvedUser.user_id,
            radio_id: formData.station[0].radio_id,
            email_address: formData.email_address.map(x => x.email).join(",") ?? [],
            run_frequency: formData.run_frequency,
            weeks: formData.weeks,
            spins_threshold: formData.spins_threshold,
            daypart_id: formData.daypart_id,
            demo_id: 1,
        };
        try {
            const response = await axiosInstance.post("reports/music_testing/", input);
            revalidator.revalidate();
            setFormData({ ...emptyFormData, email_address: resolvedUser.email ? [{ email: resolvedUser.email }] : [] });
            setErrorCode(null);
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
        }
    }

    async function handleFormSubmit(event) {
        event.preventDefault();
        const resolvedUser = (await userInfo).data;
        const input = {
            user_id: resolvedUser.user_id,
            radio_ids: formData.station.map(x => x.radio_id),
            frequency: formData.run_frequency,
            weeks_to_check: formData.number_weeks,
            min_spins: formData.minSpins,
            max_spins: formData.maxSpins,
            daypart_id: formData.daypartId ?? 1,
            demo_id: formData.demo_id,
            email_addresses: formData.email_address?.map(x => x.email) ?? [],
        };

        try {
            const response = await axiosInstance.post("reports/music_testing/", input);
            revalidator.revalidate();
            setFormData({ ...formData, email_address: resolvedUser.email ? [{ email: resolvedUser.email }] : [] });
            setErrorCode(null);
            toast.success(`Music Test Report has been created.`, { autoClose: 5000 });
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
            toast.error(`Error trying to create a Music Test Report.`);
        }
    }

    useEffect(() => {
        Promise.all([radios, competitorRadios]).then(([resolvedRadios, resolvedCompetitorRadios]) => {
            const mainRadios = resolvedRadios?.data?.map(r => ({ radio_id: r.radio_id, radio_name: r.radio_name })) ?? [];
            const competitors = resolvedCompetitorRadios?.data?.map(r => ({ radio_id: r.radio_id, radio_name: r.radio_name })) ?? [];

            const mergedRadios = [...mainRadios, ...competitors];
            const uniqueRadiosMap = new Map();
            mergedRadios.forEach(r => {
                if (!uniqueRadiosMap.has(r.radio_id)) {
                    uniqueRadiosMap.set(r.radio_id, r);
                }
            });

            setAllRadios(Array.from(uniqueRadiosMap.values()));
        });
    }, [radios, competitorRadios]);

    function getMarketDisplayNames(report) {
        const names = allRadios.filter(r => report.radio_ids.includes(r.radio_id)).map(r => r.radio_name);

        return names.length > 0 ? names.join(", ") : "Unknown";
    }

    function getTruncatedRadioNames(names, maxWidth, font = "bold 16px Arial") {
        if (!names || names.length === 0) return "";

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        context.font = font;

        let text = "";
        for (let i = 0; i < names.length; i++) {
            const newText = text ? text + ", " + names[i] : names[i];
            const width = context.measureText(newText).width;
            if (width > maxWidth - 30) {
                return text + (i < names.length ? "…" : "");
            }
            text = newText;
        }

        return text;
    }

    function mapReportToEdit(report) {
        return {
            ...report,
            run_frequency: report.frequency ?? 0,
            number_weeks: report.weeks_to_check ?? 0,
            minSpins: report.min_spins ?? 0,
            maxSpins: report.max_spins ?? 0,
            email_address: (report.email_addresses || []).map(e => (typeof e === "string" ? { email: e } : e)),
        };
    }

    async function handleSaveClick(report) {
        const input = {
            user_id: report.user_id,
            demo_id: report.demo_id,
            daypart_id: report.daypart_id,
            radio_ids: report.radio_ids,
            hide_deltas: report.hide_deltas,
            hide_invalid: report.hide_invalid,
            frequency: report.run_frequency,
            weeks_to_check: report.number_weeks,
            min_spins: report.minSpins,
            max_spins: report.maxSpins,
            email_addresses: report.email_address?.map(x => x.email) ?? [],
        };
        try {
            await axiosInstance.put("/reports/music_testing/" + report.report_id, input);
            setSelectedReport(null);
            revalidator.revalidate();
            toast.success("Report updated successfully!");
        } catch (error) {
            console.error(error);
            toast.error("Error updating report.");
        }
    }

    async function deleteMusicTestingReport(id) {
        try {
            const response = await axiosInstance.delete("/reports/music_testing/" + id);
            revalidator.revalidate();
            setDeleteReportId(null);
            setErrorCode(null);
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
        }
    }

    const musictTestingReportsMemo = useMemo(() => {
        if (!reportsData) return [];

        return reportsData.filter(x => {
            const emails = Array.isArray(x.email_addresses) ? x.email_addresses.join(",") : x.email_addresses || "";
            const minSpins = x.min_spins?.toString() || "";
            const maxSpins = x.max_spins?.toString() || "";
            const frequency = x.frequency?.toString() || "";
            const weeks = x.weeks_to_check?.toString() || "";
            const search = searchValue.toLowerCase().trim();
            const radioNames = allRadios.filter(r => x.radio_ids?.includes(r.radio_id)).map(r => r.radio_name.toLowerCase());
            const namesJoined = radioNames.join(", ");

            return (
                emails.toLowerCase().includes(search) ||
                minSpins.includes(search) ||
                maxSpins.includes(search) ||
                frequency.includes(search) ||
                weeks.includes(search) ||
                namesJoined.includes(search)
            );
        });
    }, [reportsData, searchValue, allRadios]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(musictTestingReportsMemo);

    function formatDemographicText(demo) {
        if (!demo) return;
        return `${demo.data_type === 1 ? "Online" : "Cover"}: ${demo.name}`;
    }

    return (
        <Container fluid className="p-4 ">
            <Row>
                <Col className="bg-white p-4">
                    <Form className="row" onSubmit={handleFormSubmit}>
                        <Col xs={12} xl className="order-xl-last mb-xl-0 ">
                            <h1 className="text-uppercase">Music Testing Reports</h1>
                            <div className="d-flex flex-column justify-content-between align-items-xl-start">
                                <p className="ps-1">See your reports below, or set up a new one with a click.</p>
                                <Button
                                    type="button"
                                    onClick={() => setAdvancedOpen(!advancedOpen)}
                                    className="btn-ra-yellow text-uppercase px-4 py-xl-1"
                                    aria-controls="music-test-advanced"
                                    aria-expanded={advancedOpen}>
                                    SET UP NEW
                                </Button>
                            </div>
                            <Collapse in={advancedOpen}>
                                <Row>
                                    <Col xs={12} xl={10}>
                                        <Row className="pt-4 " id="music-test-advanced ">
                                            <Suspense fallback={<span>Loading...</span>}>
                                                <Await resolve={radios}>
                                                    {resolvedRadios => (
                                                        <Form.Group controlId="music-test-report-stations" as={Col} className="mb-3 mb-xl-0 col-12 col-xl-3">
                                                            <Form.Label>Station</Form.Label>
                                                            <Typeahead
                                                                id="music-test-reports-station"
                                                                options={resolvedRadios.data}
                                                                labelKey={"radio_name"}
                                                                highlightOnlyResult
                                                                placeholder="Select station..."
                                                                clearButton={true}
                                                                onChange={value => setFormData({ ...formData, station: value })}
                                                                selected={formData.station}
                                                                multiple={true}
                                                            />
                                                        </Form.Group>
                                                    )}
                                                </Await>
                                            </Suspense>
                                            <Suspense>
                                                <Await resolve={dayparts}>
                                                    {resolvedDayparts => (
                                                        <Form.Group controlId="music-test-daypart" as={Col} className="mb-3 mb-xl-0 col-12 col-xl-3">
                                                            <Form.Label>Daypart</Form.Label>
                                                            <Form.Select
                                                                value={formData.daypart_id}
                                                                onChange={e =>
                                                                    setFormData({
                                                                        ...formData,
                                                                        daypart_id: Number(e.target.value),
                                                                    })
                                                                }
                                                                disabled={!formData.station.length}>
                                                                <option value={"placeholder"} disabled>
                                                                    {formData.station.length ? "Select daypart..." : "Select station first..."}
                                                                </option>
                                                                {findRadioDayparts(resolvedDayparts.data, formData.station[0])
                                                                    .sort((a, b) => a.daypart_id - b.daypart_id)
                                                                    .map(x => (
                                                                        <option key={x.daypart_id} value={x.daypart_id}>
                                                                            {formatDaypartText(x)}
                                                                        </option>
                                                                    ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    )}
                                                </Await>
                                            </Suspense>
                                        </Row>

                                        <Row xs={12} xl={4} className="my-0 my-xl-3 row-cols-md-3 row-cols-1">
                                            <FrequencyPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                            <WeeksPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                            <MinMaxSpinsPicker filterData={formData} setFilterData={setFormData} name={moduleName} />{" "}
                                        </Row>
                                        <div className="my-2" />
                                        <Row>
                                            <EmailAddressPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                        </Row>
                                    </Col>
                                    <Col xs={12} xl={2} className="text-center text-xl-end align-self-end ms-auto">
                                        <Button
                                            type="submit"
                                            className="btn-ra-yellow text-uppercase px-5"
                                            disabled={!formData.station.length || !formData.email_address.length || formData.daypart_id === "placeholder"}>
                                            SET UP
                                        </Button>
                                    </Col>
                                </Row>
                            </Collapse>
                        </Col>
                    </Form>
                </Col>
            </Row>
            <hr className="m-2"></hr>
            <Row>
                <Col className="bg-white p-4">
                    {!reportsData?.length > 0 && !errorCode && (
                        <Container fluid className="p-4">
                            <Row>
                                <Col xs={12} md={12} lg={8}>
                                    <h6 className="fw-bold list-unstyled">What can you do with Music Test Reports?</h6>
                                    <ul>
                                        <li>Automate and share your music testing lists</li>
                                        <li>Choose from one week to one entire year’s worth of data</li>
                                        <li>Set minimum spins to help filter your lists</li>
                                    </ul>
                                    <h6 className="fw-bold">Nice to know</h6>
                                    <ul>
                                        <li>Downloads available as Excel, CSV, or HTML</li>
                                        <li>Add any number of emails, even if they don’t have a RadioAnalyzer login</li>
                                        <li>Dayparts can be useful for unique perspectives, but be sure to have enough spins on the song to get a good read</li>
                                    </ul>

                                    <p>
                                        If you need further assistance in using Music Test Report, please contact{" "}
                                        <a
                                            className="text-dark fw-bold "
                                            href="mailto:training@radioanalyzer.com?subject=Training%20for%20Music%20Test%20Reports"
                                            target=" blank">
                                            training@radioanalyzer.com
                                        </a>
                                    </p>
                                </Col>
                                <Col xs={12} md={12} xl={4}>
                                    <div className="iframe-styling">
                                        <iframe
                                            width="100%"
                                            height="100%"
                                            src="https://www.youtube.com/embed/JEYOVS0Tib4?si=7WlzC9WmWMXY1dtZ"
                                            title="How to use
                                            Song Delta Reports"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;
                                            web-share"
                                            allowFullScreen></iframe>
                                    </div>
                                </Col>
                            </Row>
                        </Container>
                    )}
                    {reportsData && errorCode && <ErrorResponse errorCode={errorCode} />}
                    {!reportsData ? (
                        <span colSpan={12}>
                            <LoadingIndicator />
                        </span>
                    ) : (
                        reportsData.length > 0 && (
                            <>
                                <Stack direction="horizontal" gap={2} className=" flex-wrap">
                                    <p>Here you'll find an overview of your existing Music Testing Reports. Add new reports on the top.</p>
                                </Stack>
                                <Stack direction="horizontal" gap={2} className="mb-3 flex-wrap">
                                    <Col xs={12} xl={8}>
                                        <InputGroup>
                                            <InputGroup.Text className="input-icon">
                                                <Search />
                                            </InputGroup.Text>
                                            <Form.Control
                                                value={searchValue}
                                                onChange={e => {
                                                    setSearchValue(e.target.value);
                                                }}
                                                placeholder="search by radio name, spins or email address"
                                            />
                                        </InputGroup>
                                    </Col>
                                </Stack>

                                <div className="row row-cols-1 row-cols-sm-1 row-cols-md-1 row-cols-lg-2 row-cols-xl-2 row-cols-xxl-3">
                                    {sortedItems.length ? (
                                        sortedItems.map((report, index) => (
                                            <div key={index} className="col mb-4">
                                                <Card className="w-100 ">
                                                    <Card.Header className="bg-ra-dark-blue text-light d-flex justify-content-between align-items-center text-center font-weight-bold">
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip id={`tooltip-${report.report_id}`}>{getMarketDisplayNames(report)}</Tooltip>}>
                                                            <div
                                                                ref={el => {
                                                                    if (el) {
                                                                        const radioNames = allRadios
                                                                            .filter(r => report.radio_ids.includes(r.radio_id))
                                                                            .map(r => r.radio_name);
                                                                        const truncated = getTruncatedRadioNames(radioNames, el.offsetWidth, "bold 16px Arial");
                                                                        el.textContent = truncated;
                                                                    }
                                                                }}
                                                                className="fw-bold fs-6 flex-grow-1 text-center"
                                                                style={{
                                                                    overflow: "hidden",
                                                                    whiteSpace: "nowrap",
                                                                }}></div>
                                                        </OverlayTrigger>
                                                        {selectedReport?.report_id === report.report_id ? (
                                                            <ButtonGroup size="sm" className="pe-1">
                                                                <Button
                                                                    variant="success"
                                                                    title="Save"
                                                                    disabled={!selectedReport?.email_address.length}
                                                                    onClick={() => handleSaveClick(selectedReport)}>
                                                                    <CheckLg />
                                                                </Button>
                                                                <Button variant="danger" title="Discard" onClick={() => setSelectedReport(null)}>
                                                                    <XLg />
                                                                </Button>
                                                            </ButtonGroup>
                                                        ) : (
                                                            <ButtonGroup size="sm" className="pe-1">
                                                                <Button
                                                                    variant="primary"
                                                                    title={"Edit"}
                                                                    onClick={() => setSelectedReport(mapReportToEdit(report))}>
                                                                    <PencilFill />
                                                                </Button>
                                                                <Button variant="danger" title={"Delete"} onClick={() => setDeleteReportId(report.report_id)}>
                                                                    <TrashFill />
                                                                </Button>
                                                            </ButtonGroup>
                                                        )}
                                                    </Card.Header>

                                                    <Card.Body>
                                                        <Card.Text>
                                                            {selectedReport?.report_id === report.report_id ? (
                                                                <>
                                                                    <Row className="mt-3 gap-2">
                                                                        <FrequencyPicker
                                                                            formData={selectedReport}
                                                                            setFormData={setSelectedReport}
                                                                            name={moduleName + "edit"}
                                                                        />
                                                                        <MinMaxSpinsPicker
                                                                            filterData={selectedReport}
                                                                            setFilterData={setSelectedReport}
                                                                            name={moduleName + "edit"}
                                                                        />
                                                                        <WeeksPicker
                                                                            formData={selectedReport}
                                                                            setFormData={setSelectedReport}
                                                                            name={moduleName + "edit"}
                                                                        />
                                                                    </Row>
                                                                </>
                                                            ) : (
                                                                <div className="text-center">
                                                                    <div className="mb-1">
                                                                        <span>
                                                                            send every <strong>{report.frequency} </strong>weeks
                                                                        </span>
                                                                    </div>
                                                                    <div className="mb-1">
                                                                        <span>
                                                                            <strong>
                                                                                {report.min_spins} –{report.max_spins !== 0 ? report.max_spins : " ∞ "}{" "}
                                                                            </strong>
                                                                            spins
                                                                        </span>
                                                                        <div className="vr  d-xl-inline-block mx-2"></div>
                                                                        <span>
                                                                            <strong>{report.weeks_to_check}</strong> weeks
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <Suspense>
                                                                            <Await resolve={demographics}>
                                                                                {resolvedDemos => {
                                                                                    return (
                                                                                        <div className="mb-1">
                                                                                            <span className="fw-bold">
                                                                                                {formatDemographicText(
                                                                                                    resolvedDemos.data.find(x => x.demo_id === report.demo_id)
                                                                                                )}
                                                                                            </span>
                                                                                        </div>
                                                                                    );
                                                                                }}
                                                                            </Await>
                                                                        </Suspense>
                                                                        <Suspense>
                                                                            <Await resolve={dayparts}>
                                                                                {resolvedDayparts => (
                                                                                    <span className="fw-bold mb">
                                                                                        {formatDaypartText(
                                                                                            resolvedDayparts.data.find(x => x.daypart_id === report.daypart_id)
                                                                                        )}
                                                                                    </span>
                                                                                )}
                                                                            </Await>
                                                                        </Suspense>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Card.Text>

                                                        <Card.Text className="music-reports-email-card-edit text-center">
                                                            {selectedReport?.report_id === report.report_id ? (
                                                                <EmailAddressPicker
                                                                    formData={selectedReport}
                                                                    setFormData={setSelectedReport}
                                                                    name={moduleName + "edit"}
                                                                    inputOnly={true}
                                                                />
                                                            ) : (
                                                                <span className="rbt-token p-2">
                                                                    {report.email_addresses.map(e => (typeof e === "string" ? e : e.email)).join(", ")}
                                                                </span>
                                                            )}
                                                        </Card.Text>
                                                    </Card.Body>
                                                </Card>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="row justify-content-center">
                                            <div className="col-12">
                                                <span>No results.</span>
                                            </div>
                                        </div>
                                    )}
                                    <ConfirmPrompt
                                        show={deleteReportId !== null}
                                        handleClose={() => setDeleteReportId(null)}
                                        handleConfirm={async () => {
                                            await deleteMusicTestingReport(deleteReportId);
                                        }}
                                    />
                                </div>
                            </>
                        )
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default MusicTestingReports;
